import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrency } from '../hooks/useCurrency';

interface Destination {
    id: string;
    name: string;
    country: string;
    continent: string;
    description: string;
    imageUrl: string;
    rating: number;
    priceRange: {
        min: number;
        max: number;
        currency: string;
    };
    bestTime: string;
    duration: string;
    highlights: string[];
    category: string;
    isPopular: boolean;
    isFeatured: boolean;
}

const Destinations: React.FC = () => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [filteredDestinations, setFilteredDestinations] = useState<Destination[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedContinent, setSelectedContinent] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [priceRange, setPriceRange] = useState([0, 5000]);
    const [selectedRating, setSelectedRating] = useState(0);
    const [sortBy, setSortBy] = useState('name');

    // Sample destinations data
    const mockDestinations: Destination[] = [
        {
            id: '1',
            name: 'Istanbul',
            country: 'Turkey',
            continent: 'Europe',
            description: 'A magical blend of East and West, where ancient mosques meet modern skyscrapers.',
            imageUrl: '/hotell.jpg.png',
            rating: 5,
            priceRange: { min: 800, max: 2000, currency: 'USD' },
            bestTime: 'March-May, September-November',
            duration: '5-7 days',
            highlights: ['Hagia Sophia', 'Blue Mosque', 'Grand Bazaar', 'Bosphorus Cruise'],
            category: 'Cultural',
            isPopular: true,
            isFeatured: true
        },
        {
            id: '2',
            name: 'Dubai',
            country: 'UAE',
            continent: 'Asia',
            description: 'A futuristic city in the desert, home to the world\'s tallest building.',
            imageUrl: '/hote12.jpg.png',
            rating: 5,
            priceRange: { min: 1200, max: 3000, currency: 'USD' },
            bestTime: 'November-March',
            duration: '4-6 days',
            highlights: ['Burj Khalifa', 'Palm Jumeirah', 'Dubai Mall', 'Desert Safari'],
            category: 'Luxury',
            isPopular: true,
            isFeatured: true
        },
        {
            id: '3',
            name: 'Paris',
            country: 'France',
            continent: 'Europe',
            description: 'The City of Light, famous for its art, fashion, gastronomy and culture.',
            imageUrl: '/hote13.jpg.png',
            rating: 5,
            priceRange: { min: 1500, max: 4000, currency: 'USD' },
            bestTime: 'April-June, September-October',
            duration: '5-7 days',
            highlights: ['Eiffel Tower', 'Louvre Museum', 'Notre-Dame', 'Champs-Élysées'],
            category: 'Cultural',
            isPopular: true,
            isFeatured: false
        },
        {
            id: '4',
            name: 'Tokyo',
            country: 'Japan',
            continent: 'Asia',
            description: 'A fascinating blend of ultramodern and traditional, offering endless discoveries.',
            priceRange: { min: 2000, max: 5000, currency: 'USD' },
            rating: 5,
            imageUrl: '/hotell.jpg.png',
            bestTime: 'March-May, September-November',
            duration: '7-10 days',
            highlights: ['Shibuya Crossing', 'Senso-ji Temple', 'Tokyo Skytree', 'Tsukiji Market'],
            category: 'Cultural',
            isPopular: false,
            isFeatured: true
        },
        {
            id: '5',
            name: 'New York',
            country: 'USA',
            continent: 'North America',
            description: 'The city that never sleeps, offering world-class entertainment and culture.',
            imageUrl: '/hote12.jpg.png',
            rating: 4,
            priceRange: { min: 1800, max: 4500, currency: 'USD' },
            bestTime: 'April-June, September-October',
            duration: '5-7 days',
            highlights: ['Times Square', 'Central Park', 'Statue of Liberty', 'Broadway'],
            category: 'Urban',
            isPopular: true,
            isFeatured: false
        },
        {
            id: '6',
            name: 'Bangkok',
            country: 'Thailand',
            continent: 'Asia',
            description: 'A vibrant city known for its street life, cultural landmarks, and shopping.',
            imageUrl: '/hote13.jpg.png',
            rating: 4,
            priceRange: { min: 600, max: 1500, currency: 'USD' },
            bestTime: 'November-March',
            duration: '4-6 days',
            highlights: ['Grand Palace', 'Wat Phra Kaew', 'Chatuchak Market', 'Chao Phraya River'],
            category: 'Cultural',
            isPopular: false,
            isFeatured: false
        },
        {
            id: '7',
            name: 'London',
            country: 'UK',
            continent: 'Europe',
            description: 'A historic city with modern attractions, world-class museums and royal palaces.',
            imageUrl: '/hotell.jpg.png',
            rating: 5,
            priceRange: { min: 1600, max: 4000, currency: 'USD' },
            bestTime: 'March-May, September-October',
            duration: '5-7 days',
            highlights: ['Big Ben', 'Buckingham Palace', 'British Museum', 'Tower Bridge'],
            category: 'Cultural',
            isPopular: true,
            isFeatured: false
        },
        {
            id: '8',
            name: 'Singapore',
            country: 'Singapore',
            continent: 'Asia',
            description: 'A modern city-state known for its cleanliness, efficiency and diverse culture.',
            imageUrl: '/hote12.jpg.png',
            rating: 5,
            priceRange: { min: 1000, max: 2500, currency: 'USD' },
            bestTime: 'February-April, July-September',
            duration: '4-5 days',
            highlights: ['Marina Bay Sands', 'Gardens by the Bay', 'Sentosa Island', 'Chinatown'],
            category: 'Urban',
            isPopular: false,
            isFeatured: true
        }
    ];

    const continents = ['All', 'Europe', 'Asia', 'North America', 'South America', 'Africa', 'Oceania'];
    const categories = ['All', 'Cultural', 'Luxury', 'Urban', 'Adventure', 'Beach', 'Mountain'];

    useEffect(() => {
        // Simulate loading
        setTimeout(() => {
            setDestinations(mockDestinations);
            setFilteredDestinations(mockDestinations);
            setIsLoading(false);
        }, 1000);
    }, []);

    useEffect(() => {
        // Apply filters
        let filtered = destinations;

        // Search term filter
        if (searchTerm) {
            filtered = filtered.filter(dest =>
                dest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                dest.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
                dest.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Continent filter
        if (selectedContinent && selectedContinent !== 'All') {
            filtered = filtered.filter(dest => dest.continent === selectedContinent);
        }

        // Category filter
        if (selectedCategory && selectedCategory !== 'All') {
            filtered = filtered.filter(dest => dest.category === selectedCategory);
        }

        // Price filter
        filtered = filtered.filter(dest => 
            dest.priceRange.min >= priceRange[0] && dest.priceRange.max <= priceRange[1]
        );

        // Rating filter
        if (selectedRating > 0) {
            filtered = filtered.filter(dest => dest.rating >= selectedRating);
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'price':
                    return a.priceRange.min - b.priceRange.min;
                case 'rating':
                    return b.rating - a.rating;
                case 'name':
                default:
                    return a.name.localeCompare(b.name);
            }
        });

        setFilteredDestinations(filtered);
    }, [destinations, searchTerm, selectedContinent, selectedCategory, priceRange, selectedRating, sortBy]);

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedContinent('All');
        setSelectedCategory('All');
        setPriceRange([0, 5000]);
        setSelectedRating(0);
        setSortBy('name');
    };

    if (isLoading) {
        return (
            <div className="destinations-page">
                <div className="container">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>{t('common.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="destinations-page">
            <div className="container">
                {/* Hero Section */}
                <motion.section 
                    className="destinations-hero"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1>{t('destinations.title', 'Destinations')}</h1>
                    <p>{t('destinations.subtitle', 'Discover amazing places around the world')}</p>
                </motion.section>

                {/* Featured Destinations */}
                <motion.section 
                    className="featured-destinations"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="section-header">
                        <h2>{t('destinations.featured', 'Featured Destinations')}</h2>
                        <p>{t('destinations.featuredDesc', 'Our top picks for unforgettable experiences')}</p>
                    </div>
                    
                    <div className="featured-grid">
                        {destinations.filter(d => d.isFeatured).map((dest, index) => (
                            <motion.div
                                key={dest.id}
                                className="featured-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                whileHover={{ y: -5, scale: 1.02 }}
                            >
                                <div className="featured-image">
                                    <img src={dest.imageUrl} alt={dest.name} />
                                    <div className="featured-badge">⭐ Featured</div>
                                    <div className="featured-rating">
                                        {'⭐'.repeat(dest.rating)}
                                    </div>
                                </div>
                                <div className="featured-content">
                                    <div className="destination-meta">
                                        <span className="country">{dest.country}</span>
                                        <span className="continent">{dest.continent}</span>
                                    </div>
                                    <h3 className="destination-name">{dest.name}</h3>
                                    <p className="destination-desc">{dest.description}</p>
                                    <div className="destination-highlights">
                                        {dest.highlights.slice(0, 3).map((highlight, idx) => (
                                            <span key={idx} className="highlight">{highlight}</span>
                                        ))}
                                    </div>
                                    <div className="destination-info">
                                        <div className="info-item">
                                            <span className="label">{t('destinations.bestTime', 'Best Time')}:</span>
                                            <span className="value">{dest.bestTime}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="label">{t('destinations.duration', 'Duration')}:</span>
                                            <span className="value">{dest.duration}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="label">{t('destinations.price', 'Price')}:</span>
                                            <span className="value price">
                                                {formatPrice(dest.priceRange.min)} - {formatPrice(dest.priceRange.max)}
                                            </span>
                                        </div>
                                    </div>
                                    <button className="btn btn-primary explore-btn">
                                        {t('destinations.explore', 'Explore')}
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.section>

                {/* Filters */}
                <motion.section 
                    className="destinations-filters"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    <div className="filters-header">
                        <h3>{t('destinations.filters', 'Filters')}</h3>
                        <button onClick={clearFilters} className="clear-filters-btn">
                            {t('destinations.clearFilters', 'Clear All')}
                        </button>
                    </div>

                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>{t('destinations.search', 'Search')}</label>
                            <input
                                type="text"
                                placeholder={t('destinations.searchPlaceholder', 'Destination, country...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="filter-input"
                            />
                        </div>

                        <div className="filter-group">
                            <label>{t('destinations.continent', 'Continent')}</label>
                            <select
                                value={selectedContinent}
                                onChange={(e) => setSelectedContinent(e.target.value)}
                                className="filter-select"
                            >
                                {continents.map(continent => (
                                    <option key={continent} value={continent}>
                                        {continent === 'All' ? t('destinations.allContinents', 'All Continents') : continent}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>{t('destinations.category', 'Category')}</label>
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="filter-select"
                            >
                                {categories.map(category => (
                                    <option key={category} value={category}>
                                        {category === 'All' ? t('destinations.allCategories', 'All Categories') : category}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>{t('destinations.priceRange', 'Price Range')}</label>
                            <div className="price-range">
                                <input
                                    type="number"
                                    placeholder="Min"
                                    value={priceRange[0]}
                                    onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                                    className="filter-input"
                                />
                                <span>-</span>
                                <input
                                    type="number"
                                    placeholder="Max"
                                    value={priceRange[1]}
                                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 5000])}
                                    className="filter-input"
                                />
                            </div>
                        </div>

                        <div className="filter-group">
                            <label>{t('destinations.minRating', 'Minimum Rating')}</label>
                            <div className="rating-filter">
                                {[1, 2, 3, 4, 5].map(rating => (
                                    <button
                                        key={rating}
                                        className={`rating-btn ${selectedRating === rating ? 'active' : ''}`}
                                        onClick={() => setSelectedRating(selectedRating === rating ? 0 : rating)}
                                    >
                                        {'⭐'.repeat(rating)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="filter-group">
                            <label>{t('destinations.sortBy', 'Sort By')}</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="filter-select"
                            >
                                <option value="name">{t('destinations.sortName', 'Name')}</option>
                                <option value="price">{t('destinations.sortPrice', 'Price')}</option>
                                <option value="rating">{t('destinations.sortRating', 'Rating')}</option>
                            </select>
                        </div>
                    </div>
                </motion.section>

                {/* All Destinations */}
                <motion.section 
                    className="all-destinations"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                >
                    <div className="section-header">
                        <h2>
                            {t('destinations.allDestinations', 'All Destinations')} ({filteredDestinations.length})
                        </h2>
                        <p>
                            {filteredDestinations.length > 0 
                                ? t('destinations.destinationsFound', 'destinations found')
                                : t('destinations.noDestinations', 'No destinations found matching your criteria')
                            }
                        </p>
                    </div>

                    {filteredDestinations.length === 0 ? (
                        <div className="no-results">
                            <div className="no-results-icon">🌍</div>
                            <h4>{t('destinations.noResultsTitle', 'No Destinations Found')}</h4>
                            <p>{t('destinations.noResultsDesc', 'Try adjusting your filters or search criteria')}</p>
                            <button onClick={clearFilters} className="btn btn-primary">
                                {t('destinations.clearFilters', 'Clear Filters')}
                            </button>
                        </div>
                    ) : (
                        <div className="destinations-grid">
                            {filteredDestinations.map((dest, index) => (
                                <motion.div
                                    key={dest.id}
                                    className="destination-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    whileHover={{ y: -3, scale: 1.01 }}
                                >
                                    <div className="card-image">
                                        <img src={dest.imageUrl} alt={dest.name} />
                                        {dest.isPopular && (
                                            <div className="popular-badge">🔥 Popular</div>
                                        )}
                                        <div className="rating-badge">
                                            {'⭐'.repeat(dest.rating)}
                                        </div>
                                    </div>
                                    <div className="card-content">
                                        <div className="destination-meta">
                                            <span className="country">{dest.country}</span>
                                            <span className="continent">{dest.continent}</span>
                                        </div>
                                        <h3 className="destination-name">{dest.name}</h3>
                                        <p className="destination-desc">{dest.description}</p>
                                        <div className="destination-category">
                                            <span className="category-tag">{dest.category}</span>
                                        </div>
                                        <div className="destination-info">
                                            <div className="info-item">
                                                <span className="label">{t('destinations.bestTime', 'Best Time')}:</span>
                                                <span className="value">{dest.bestTime}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">{t('destinations.duration', 'Duration')}:</span>
                                                <span className="value">{dest.duration}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="label">{t('destinations.price', 'Price')}:</span>
                                                <span className="value price">
                                                    {formatPrice(dest.priceRange.min)} - {formatPrice(dest.priceRange.max)}
                                                </span>
                                            </div>
                                        </div>
                                        <button className="btn btn-primary explore-btn">
                                            {t('destinations.explore', 'Explore')}
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </motion.section>
            </div>
        </div>
    );
};

export default Destinations;

